import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Pencil, Image as ImageIcon, FileText, Video, Loader2, Upload } from 'lucide-react';
import {
  parseVariableDefs,
  buildInitialVars,
  renderBody,
  extractPlaceholderIndices,
  splitTemplateBody,
  CONTACT_FIELD_LABELS,
  type ContactLike,
} from '@kalkuel-inbox/shared';
import { Button, InlineFill } from './ui';
import { TemplatePreview } from './TemplatePreview';
import { templateDisplayName } from '../lib/templateDisplay';
import { api, ApiError } from '../lib/api';
import { toast } from 'sonner';
import type { Template, TemplateButton } from '../types';

/**
 * „Vorlage ausfüllen" — editierbarer LÜCKENTEXT statt getrennter Variablen-Felder.
 *
 * Die Nachricht wird im Fluss angezeigt: fester Text als Spans, jede Variable als
 * Inline-Eingabefeld an genau ihrer Stelle. CONTACT-Variablen (Typ A) sind aus dem Kontakt
 * vorbefüllt (editierbar), INPUT-Variablen (Typ B) leer + amber hervorgehoben. Der Lückentext
 * IST die Vorschau.
 *
 * Schalter „frei bearbeiten": gibt die ganze Nachricht als Textarea frei (auch der feste Text)
 * — das Ergebnis wird dann als normale Nachricht (Freitext) gesendet, NICHT als formale Vorlage.
 *
 * Volle Komponenten-Parität (nur aktiv, wenn die Vorlage die Komponente HAT — sonst unverändert):
 *   - Header-Text-Variable: zusätzliches „Überschrift"-Feld OBERHALB des Body-Lückentexts,
 *     wenn `headerType === 'TEXT'` und `headerText` ein `{{1}}` enthält → `headerVars: [wert]`.
 *   - Medien-Header (IMAGE/DOCUMENT/VIDEO): Hinweis, dass das Beispiel-Medium mitgesendet wird
 *     (Worker-Fallback auf `headerMediaSampleUrl`). Optional „anderes Medium wählen" → Upload
 *     setzt `headerMedia: { mediaId }`.
 *   - Dynamische URL-Buttons: ein `{{1}}` in der Button-URL → Eingabefeld → `buttonParams`.
 *
 * Ergebnis über `onConfirm`:
 *   { mode: 'template', vars, headerVars?, headerMedia?, buttonParams? }  → echte WhatsApp-Vorlage
 *   { mode: 'freeform', text }                                            → freier Text
 *
 * Body-only-Vorlagen (die 10 Bestands-Vorlagen) liefern weiterhin NUR `vars` — alle
 * Zusatzfelder bleiben `undefined`, der bestehende Smart-Send-/Lückentext-/Frei-Modus-Flow
 * ist davon völlig unberührt.
 */

export type TemplateFillResult =
  | {
      mode: 'template';
      vars: string[];
      headerVars?: string[];
      headerMedia?: { mediaId: string };
      buttonParams?: Array<{ subType: 'url' | 'quick_reply'; index: number; value: string }>;
    }
  | { mode: 'freeform'; text: string };

function maxPlaceholderIndex(template: Template): number {
  const fromBody = extractPlaceholderIndices(template.body);
  const fromDefs = parseVariableDefs(template.variableDefs).map((d) => d.index);
  const all = [...fromBody, ...fromDefs];
  return all.length === 0 ? 0 : Math.max(...all);
}

/** Hat die Vorlage einen TEXT-Header mit Variable ({{1}})? Dann braucht der Header ein Eingabefeld. */
function hasHeaderTextVar(template: Template): boolean {
  return (
    template.headerType === 'TEXT' &&
    typeof template.headerText === 'string' &&
    /\{\{\s*\d+\s*\}\}/.test(template.headerText)
  );
}

/** Hat die Vorlage einen Medien-Header (IMAGE/DOCUMENT/VIDEO)? */
function hasMediaHeader(template: Template): boolean {
  return (
    template.headerType === 'IMAGE' ||
    template.headerType === 'DOCUMENT' ||
    template.headerType === 'VIDEO'
  );
}

/**
 * Dynamische URL-Buttons: URL-Buttons mit `{{1}}` in der Ziel-URL brauchen einen per-Send-Wert.
 * `index` = Position des Buttons in `template.buttons` (Meta-Komponenten-Index, wie vom Worker
 * 1:1 als `index` durchgereicht). Statische Buttons / Quick-Reply brauchen nichts.
 */
function dynamicUrlButtons(template: Template): Array<{ index: number; button: TemplateButton }> {
  const out: Array<{ index: number; button: TemplateButton }> = [];
  const buttons = template.buttons ?? [];
  buttons.forEach((button, index) => {
    if (button.type === 'URL' && typeof button.url === 'string' && /\{\{\s*\d+\s*\}\}/.test(button.url)) {
      out.push({ index, button });
    }
  });
  return out;
}

/** Ob die Vorlage überhaupt einen Ausfüll-Dialog braucht (Platzhalter ODER Komponenten-Eingaben?). */
export function needsTemplateVars(template: Template): boolean {
  return (
    maxPlaceholderIndex(template) > 0 ||
    hasHeaderTextVar(template) ||
    hasMediaHeader(template) ||
    dynamicUrlButtons(template).length > 0
  );
}

interface Props {
  template: Template;
  contact: ContactLike | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (result: TemplateFillResult) => void;
}

export function TemplateVarsDialog({ template, contact, open, onClose, onConfirm }: Props) {
  const defs = useMemo(() => parseVariableDefs(template.variableDefs), [template.variableDefs]);
  const maxIndex = useMemo(() => maxPlaceholderIndex(template), [template]);
  const segments = useMemo(() => splitTemplateBody(template.body), [template.body]);
  const defByIndex = useMemo(() => {
    const map = new Map<number, (typeof defs)[number]>();
    for (const d of defs) map.set(d.index, d);
    return map;
  }, [defs]);

  const indices = useMemo(() => Array.from({ length: maxIndex }, (_, i) => i + 1), [maxIndex]);

  // Komponenten-Erkennung (memoisiert — reine Funktionen der Vorlage).
  const headerVarActive = useMemo(() => hasHeaderTextVar(template), [template]);
  const mediaHeaderActive = useMemo(() => hasMediaHeader(template), [template]);
  const urlButtons = useMemo(() => dynamicUrlButtons(template), [template]);
  // Hat die Vorlage ÜBERHAUPT Zusatz-Komponenten? Dann zeigen wir die WhatsApp-Vorschau —
  // sonst (reine Body-Vorlage) bleibt es bei der gewohnten reinen Lückentext-Ansicht.
  const hasComponents = headerVarActive || mediaHeaderActive || urlButtons.length > 0 || !!template.footer;

  // Variablen-Werte (CONTACT vorbefüllt, INPUT leer) + Frei-Modus + Frei-Text.
  const [vars, setVars] = useState<Record<string, string>>({});
  const [freeMode, setFreeMode] = useState(false);
  const [freeText, setFreeText] = useState('');
  // Header-Text-Variable (genau eine: das {{1}} im Header). Body-Vorlagen lassen das leer/ungenutzt.
  const [headerVar, setHeaderVar] = useState('');
  // Dynamische URL-Button-Werte je Button-Index.
  const [buttonVals, setButtonVals] = useState<Record<number, string>>({});
  // Optional gewähltes per-Send-Medium für den Medien-Header (sonst greift der Sample-Fallback).
  const [headerMediaId, setHeaderMediaId] = useState<string | null>(null);
  const [headerMediaName, setHeaderMediaName] = useState<string | null>(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);

  // Fokus-Refs: das ERSTE leere Pflichtfeld in visueller Reihenfolge (Header zuerst, dann Body)
  // bekommt den Fokus. Header-Var-Feld kann also das erste Feld sein.
  const headerFieldRef = useRef<HTMLInputElement | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  // Verhindert, dass die „nichts auszufüllen"-Auto-Bestätigung mehr als einmal feuert
  // (sonst drohte ein Doppel-Versand). Wird bei jedem Schließen zurückgesetzt.
  const autoConfirmedRef = useRef(false);
  // One-Shot-Guard auch für die MANUELLE Bestätigung (Senden-Klick / Enter): ein schneller
  // Doppelklick oder Enter+Klick darf onConfirm — und damit den Versand — nur EINMAL auslösen.
  const confirmedRef = useRef(false);

  // Bei jedem Öffnen frisch initialisieren.
  useEffect(() => {
    if (!open) {
      autoConfirmedRef.current = false;
      confirmedRef.current = false;
      return;
    }
    const initial = buildInitialVars(defs, contact);
    for (const idx of indices) if (!(String(idx) in initial)) initial[String(idx)] = '';
    setVars(initial);
    setFreeMode(false);
    setFreeText('');
    // Header-Variable hat im Schema keine eigene VariableDef → kein CONTACT-Prefill möglich, leer.
    setHeaderVar('');
    setButtonVals({});
    setHeaderMediaId(null);
    setHeaderMediaName(null);
    setMediaUploading(false);
  }, [open, defs, contact, indices]);

  const orderedVars = useMemo(() => indices.map((idx) => vars[String(idx)] ?? ''), [indices, vars]);

  // Welches BODY-Var-Segment beim Öffnen den Fokus bekommt: das erste LEERE INPUT-Feld in
  // Body-Reihenfolge (= Tab-/Leserichtung), sonst das erste Var-Segment überhaupt.
  const focusSegmentIndex = useMemo(() => {
    const initial = buildInitialVars(defs, contact);
    let firstAny = -1;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg.type !== 'var') continue;
      if (firstAny === -1) firstAny = i;
      const def = defByIndex.get(seg.index);
      const isInput = def?.source !== 'CONTACT';
      const prefilled = initial[String(seg.index)] ?? '';
      if (isInput && prefilled === '') return i; // erstes leeres Eingabefeld
    }
    return firstAny;
  }, [segments, defs, contact, defByIndex]);

  // Beim Öffnen Fokus setzen: Header-Var-Feld hat Vorrang (steht visuell zuerst und ist immer leer),
  // sonst das erste leere Body-Feld.
  useEffect(() => {
    if (!open || freeMode) return;
    const id = requestAnimationFrame(() => {
      if (headerVarActive && headerFieldRef.current) headerFieldRef.current.focus();
      else firstFieldRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open, freeMode, headerVarActive]);

  // Esc schließt — Propagation stoppen, damit ein darunterliegendes Modal nicht mitschließt.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Sicherheitsnetz: nichts auszufüllen (keine Body-Platzhalter UND keine Komponenten-Eingaben)
  // → sofort als (leere) Vorlage bestätigen, aber nur EINMAL pro Öffnen (kein Doppel-Versand).
  const nothingToFill = maxIndex === 0 && !headerVarActive && !mediaHeaderActive && urlButtons.length === 0;
  useEffect(() => {
    if (!open || !nothingToFill) return;
    if (autoConfirmedRef.current) return;
    autoConfirmedRef.current = true;
    onConfirm({ mode: 'template', vars: [] });
  }, [open, nothingToFill, onConfirm]);

  // Eingesetzte Werte für die optionale WhatsApp-Vorschau (Body-Platzhalter mit aktuellem Stand).
  // ⛔ VOR dem frühen `return null`: Hooks müssen in jedem Render gleich oft laufen, sonst
  //    bricht React beim Öffnen ab (TemplateVarsDialog.test.tsx).
  const filledVars = useMemo(() => {
    const out: Record<number, string> = {};
    indices.forEach((idx) => {
      const v = vars[String(idx)] ?? '';
      if (v.trim()) out[idx] = v;
    });
    return out;
  }, [indices, vars]);

  if (!open || nothingToFill) return null;

  function toggleFreeMode() {
    if (!freeMode) {
      // In den Frei-Modus: aktuellen Lückentext-Stand als Volltext übernehmen.
      setFreeText(renderBody(template.body, orderedVars));
      setFreeMode(true);
    } else {
      setFreeMode(false);
    }
  }

  async function onMediaPicked(file: File) {
    setMediaUploading(true);
    try {
      const asset = await api.upload<{ id: string; mimeType: string; filename: string | null }>(
        '/api/uploads',
        (() => {
          const form = new FormData();
          form.append('file', file);
          return form;
        })(),
      );
      setHeaderMediaId(asset.id);
      setHeaderMediaName(asset.filename ?? file.name);
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) toast.error('Datei zu groß.');
      else toast.error(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setMediaUploading(false);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    }
  }

  function confirm() {
    // Doppel-Versand-Guard: nach der ersten Bestätigung schließt der Dialog ohnehin, aber ein
    // schneller zweiter Click/Enter im selben Frame würde onConfirm sonst erneut feuern.
    if (confirmedRef.current) return;
    confirmedRef.current = true;
    if (freeMode) {
      onConfirm({ mode: 'freeform', text: freeText });
      return;
    }
    const result: TemplateFillResult = { mode: 'template', vars: orderedVars };
    if (headerVarActive) result.headerVars = [headerVar];
    if (mediaHeaderActive && headerMediaId) result.headerMedia = { mediaId: headerMediaId };
    if (urlButtons.length > 0) {
      const params = urlButtons
        .map((b) => ({ subType: 'url' as const, index: b.index, value: buttonVals[b.index] ?? '' }))
        // Nur Buttons mit einem Wert mitschicken (leere {{1}} würde Meta ablehnen).
        .filter((p) => p.value.trim() !== '');
      if (params.length > 0) result.buttonParams = params;
    }
    onConfirm(result);
  }

  const MediaIcon =
    template.headerType === 'IMAGE' ? ImageIcon : template.headerType === 'VIDEO' ? Video : FileText;
  const mediaKindLabel =
    template.headerType === 'IMAGE' ? 'Bild' : template.headerType === 'VIDEO' ? 'Video' : 'Dokument';

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-6 pt-20"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-vars-title"
        className="flex max-h-[80vh] w-full max-w-md flex-col rounded-xl bg-white shadow-brand-lg"
      >
        <header className="flex items-center justify-between border-b border-kalkuel-border px-5 py-3">
          <div className="min-w-0">
            <div className="kalkuel-eyebrow">Vorlage ausfüllen</div>
            <div
              id="template-vars-title"
              className="truncate text-sm font-semibold text-kalkuel-primary"
            >
              {templateDisplayName(template)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Schließen"
            className="rounded-pill p-1 text-kalkuel-muted hover:bg-kalkuel-surface hover:text-kalkuel-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-kalkuel-accent/40"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex items-center justify-between gap-2 border-b border-kalkuel-border px-5 py-2">
          <span className="text-[11px] text-kalkuel-muted">
            {freeMode
              ? 'Frei bearbeitet → wird als normale Nachricht gesendet.'
              : 'Tippe direkt in die hervorgehobenen Felder.'}
          </span>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-kalkuel-primary">
            <input
              type="checkbox"
              checked={freeMode}
              onChange={toggleFreeMode}
              className="h-3.5 w-3.5 accent-kalkuel-accent"
            />
            <Pencil size={12} />
            Frei bearbeiten
          </label>
        </div>

        <div className="overflow-y-auto p-5">
          {freeMode ? (
            <textarea
              autoFocus
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              className="h-40 w-full resize-none rounded-md border border-kalkuel-border p-3 text-sm focus:border-kalkuel-accent focus:outline-none focus:ring-2 focus:ring-kalkuel-accent/30"
            />
          ) : (
            <div className="space-y-4">
              {/* Header-Text-Variable: „Überschrift"-Feld OBERHALB des Body-Lückentexts. */}
              {headerVarActive && (
                <div>
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-kalkuel-muted">
                    Überschrift
                  </label>
                  <input
                    ref={headerFieldRef}
                    type="text"
                    value={headerVar}
                    onChange={(e) => setHeaderVar(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        confirm();
                      }
                    }}
                    placeholder={template.headerTextExample ?? 'Überschrift eingeben…'}
                    aria-label="Überschrift der Vorlage"
                    className="w-full rounded-field border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 placeholder:text-amber-700/50 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                  />
                </div>
              )}

              {/* Medien-Header: Hinweis (Beispiel-Medium) + optionaler Upload eines anderen Mediums. */}
              {mediaHeaderActive && (
                <div className="rounded-field border border-kalkuel-border bg-kalkuel-surface px-3 py-2.5">
                  <div className="flex items-center gap-2 text-xs text-kalkuel-primary">
                    <MediaIcon size={15} className="shrink-0 text-kalkuel-accent-strong" aria-hidden />
                    <span className="min-w-0 flex-1">
                      {headerMediaId ? (
                        <>
                          {mediaKindLabel}:{' '}
                          <span className="font-medium">{headerMediaName ?? 'eigenes Medium'}</span>
                        </>
                      ) : (
                        <>Das {mediaKindLabel}-Beispiel der Vorlage wird mitgesendet.</>
                      )}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      ref={mediaInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onMediaPicked(f);
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={mediaUploading}
                    >
                      {mediaUploading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Upload size={12} />
                      )}
                      Anderes {mediaKindLabel} wählen
                    </Button>
                    {headerMediaId && (
                      <button
                        type="button"
                        onClick={() => {
                          setHeaderMediaId(null);
                          setHeaderMediaName(null);
                        }}
                        className="text-[11px] text-kalkuel-muted underline-offset-2 hover:text-kalkuel-primary hover:underline"
                      >
                        zurück zum Beispiel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Body-Lückentext — unverändert. */}
              <div className="whitespace-pre-wrap text-sm leading-8 text-kalkuel-primary">
                {segments.map((seg, i) => {
                  if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
                  const def = defByIndex.get(seg.index);
                  const isInput = def?.source !== 'CONTACT';
                  const label = def?.label ?? `Variable ${seg.index}`;
                  const contactHint =
                    def?.source === 'CONTACT' && def.contactField
                      ? `aus Kontakt: ${CONTACT_FIELD_LABELS[def.contactField] ?? def.contactField}`
                      : 'manuelle Eingabe';
                  return (
                    <InlineFill
                      key={i}
                      value={vars[String(seg.index)] ?? ''}
                      onChange={(v) => setVars((prev) => ({ ...prev, [String(seg.index)]: v }))}
                      placeholder={label}
                      highlight={isInput}
                      title={`${label} (${contactHint})`}
                      ariaLabel={label}
                      inputRef={i === focusSegmentIndex ? firstFieldRef : undefined}
                      onEnter={confirm}
                    />
                  );
                })}
              </div>

              {/* Dynamische URL-Buttons: ein Feld je Button mit {{1}} in der URL. */}
              {urlButtons.length > 0 && (
                <div className="space-y-2">
                  {urlButtons.map(({ index, button }) => (
                    <div key={index}>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-kalkuel-muted">
                        Button-Link: {button.text || `Button ${index + 1}`}
                      </label>
                      <input
                        type="text"
                        value={buttonVals[index] ?? ''}
                        onChange={(e) =>
                          setButtonVals((prev) => ({ ...prev, [index]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            confirm();
                          }
                        }}
                        placeholder="dynamischer URL-Teil…"
                        aria-label={`Dynamischer URL-Teil für Button ${button.text || index + 1}`}
                        className="w-full rounded-field border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 placeholder:text-amber-700/50 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                      />
                      {button.url && (
                        <div className="mt-0.5 truncate text-[10px] text-kalkuel-muted" title={button.url}>
                          {button.url}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* WhatsApp-artige Vorschau — nur bei Vorlagen MIT Zusatz-Komponenten, damit der
                  Nutzer Header/Body/Buttons mit eingesetzten Werten sieht. Reine Body-Vorlagen
                  brauchen sie nicht (der Lückentext IST dort die Vorschau). */}
              {hasComponents && (
                <div className="border-t border-kalkuel-border pt-3">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-kalkuel-muted">
                    Vorschau
                  </div>
                  <div className="rounded-lg bg-kalkuel-surface p-2">
                    <TemplatePreview
                      template={{
                        body: template.body,
                        headerType: template.headerType,
                        headerText: headerVarActive
                          ? renderBody(template.headerText ?? '', [headerVar])
                          : template.headerText,
                        headerMediaFilename: headerMediaName,
                        footer: template.footer,
                        buttons: template.buttons,
                        variableDefs: template.variableDefs,
                      }}
                      filledVars={filledVars}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-kalkuel-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={confirm} disabled={mediaUploading}>
            Senden
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
